// /pages/FleetManagement/Drivers/index.tsx
// import React, { useState, useCallback, useMemo, useRef } from 'react';
// import { 
//     Box, Heading, Text, HStack, VStack, InputGroup, Input, 
//     InputRightElement, IconButton, Switch, Card, CardHeader, 
//     CardBody, Badge, Wrap, WrapItem 
// } from '@chakra-ui/react';
// import { Search } from 'lucide-react';
// import { useNavigate } from 'react-router-dom';
// import { AdminLayout } from '@/components/layout/AdminLayout';
// import { apiClient } from '@/lib/api/client';
// import { useToastNotification } from '@/lib/utils/useToastNotification';
// import { DataTable, type DataTableRef, type DataTableOptions } from '@/components/tables';
// import { useAccessCheck } from '@/hooks/useAccess';
// import { useThemeColors } from '@/hooks/useThemeColors';
// import { ActionButtons } from '@/components/common/ActionButtons';

// const DriversPage: React.FC = () => {
//     const navigate = useNavigate();
//     const colors = useThemeColors();
//     const access = useAccessCheck();
//     const tableRef = useRef<DataTableRef>(null);
//     const searchRef = useRef<HTMLInputElement>(null);
//     const { showError, showSuccess } = useToastNotification();

//     const [searchQuery, setSearchQuery] = useState('');
//     const [updatingStatus, setUpdatingStatus] = useState<Record<number, boolean>>({});

//     const handleSearch = () => {
//         const query = searchRef.current?.value || '';
//         setSearchQuery(query); 
//         tableRef.current?.applyFilter('search', query); 
//     };

//     const handleStatusToggle = useCallback(async (driverId: number, currentStatus: number) => {
//         setUpdatingStatus((prev) => ({ ...prev, [driverId]: true }));
//         try {
//             const endpoint = currentStatus === 1 
//                 ? `/drivers/deactivate/${driverId}` 
//                 : `/drivers/activate/${driverId}`;
            
//             await apiClient.patch(endpoint); 
//             showSuccess('Success', `Driver status updated successfully.`);
//             tableRef.current?.reload();
//         } catch (error: any) {
//             showError(error);
//         } finally {
//             setUpdatingStatus((prev) => {
//                 const updated = { ...prev };
//                 delete updated[driverId];
//                 return updated;
//             });
//         }
//     }, [showSuccess, showError]);

//     const tableOptions: DataTableOptions = useMemo(
//         () => ({
//             dataSource: '/drivers', 
//             primaryKey: 'id',
//             showSelection: false, 
//             softDelete: true, 
//             displayInModal: true,
//             noRecordText: 'No drivers found',
            
//             dataFields: {
//                 first_name: { name: 'First Name', withSort: true },
//                 last_name: { name: 'Last Name', withSort: true },
//                 contact_number: { name: 'Contact Number', withSort: false, maxWidth: '150px' },
//                 // 🌟 Match your API key name here
//                 assigned_truck_plate_numbers: { name: 'Assigned Trucks', withSort: false },
//                 is_active: { name: 'STATUS', withSort: false, maxWidth: '100px' },
//             },

//             actions: [
//                 {
//                     label: 'Edit Driver',
//                     action: 'Edit',
//                     icon: 'Edit',
//                     can_do: access.can_edit,
//                 },
//             ],

//             otherActions: [
//               {
//                 name: 'Waybill History',
//                 link: '/fleet-management/waybill-history',
//               },
//               {
//                 name: 'Cash Advance History',
//                 link: '/fleet-management/ca-history',
//               },
//             ]

//         }),
//         [access.can_edit]
//     );

//     const customRenderers = useMemo(
//         () => ({
//             // 🌟 Updated Renderer to handle the Array of Plate Numbers
//             assigned_truck_plate_numbers: (value: string[]) => {
//                 if (!value || value.length === 0) return <Text color="gray.400" fontSize="xs">None</Text>;
                
//                 return (
//                     <Wrap spacing={1}>
//                         {value.map((plate) => (
//                             <WrapItem key={plate}>
//                                 <Badge colorScheme="blue" variant="subtle" fontSize="xs">
//                                     {plate}
//                                 </Badge>
//                             </WrapItem>
//                         ))}
//                     </Wrap>
//                 );
//             },
//             is_active: (value: any, row: any) => {
//                 const statusValue = Number(value);
//                 const isUpdating = updatingStatus[row.id] || false;
//                 return (
//                     <Switch
//                         isChecked={statusValue === 1}
//                         isDisabled={isUpdating || !access.can_edit}
//                         colorScheme={statusValue === 1 ? 'green' : 'red'}
//                         onChange={() => handleStatusToggle(row.id, statusValue)}
//                     />
//                 );
//             },
//         }),
//         [updatingStatus, handleStatusToggle, access.can_edit]
//     );

//     const permissions = useMemo(() => ({
//         can_edit: access.can_edit,
//         can_create: access.can_create,
//         can_delete: access.can_delete
//     }), [access]);

//     return (
//         <AdminLayout>
//             <Box p={4} h="100%" overflow="hidden" display="flex" flexDirection="column">
//                 <VStack spacing={4} align="stretch" flex="1" overflow="hidden" minH={0}>
//                     <Card flexShrink={0} display="flex" flexDirection="column" flex="1" minH={0} overflow="hidden">
//                         <CardHeader py={3}>
//                             <Heading size="md" fontWeight="bold" mb={1}>Driver Management</Heading>
//                             <Text fontSize="sm" color={colors.text}>Manage drivers and their truck assignments.</Text>
//                         </CardHeader>
                        
//                         <CardHeader py={0} px={6}>
//                             <HStack justify="space-between" align="center" width="100%" pb={4}>
//                                 <Box flex="0 1 auto" minW="250px" maxW="400px">
//                                     <InputGroup size="sm">
//                                         <Input
//                                             ref={searchRef}
//                                             placeholder="Search drivers..."
//                                             onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
//                                             defaultValue={searchQuery}
//                                         />
//                                         <InputRightElement width="2.5rem">
//                                             <IconButton
//                                                 aria-label="Search"
//                                                 icon={<Search size={16} />}
//                                                 size="sm"
//                                                 onClick={handleSearch}
//                                                 variant="ghost"
//                                             />
//                                         </InputRightElement>
//                                     </InputGroup>
//                                 </Box>
//                                 <Box pb={2}>
//                                   <ActionButtons pathname="/fleet-management/drivers" createLabel="Add New Driver" />
//                                 </Box>
//                             </HStack>
//                         </CardHeader>
                        
//                         <CardBody
//                             p={0} pt={1} pb={4} mx={6} flex="1" overflow="hidden" display="flex" flexDirection="column"
//                             sx={{ 'th:first-of-type, td:first-of-type': { display: 'none' } }}
//                         >
//                             <Box h="100%" display="flex" flexDirection="column" minH={0}>
//                                 <DataTable
//                                     ref={tableRef}
//                                     options={tableOptions}
//                                     access={permissions}
//                                     customRenderers={customRenderers}
//                                     onOpenModal={(idObj) => {
//                                         const dId = typeof idObj === 'object' ? idObj.id : idObj;
//                                         if (dId) navigate(`/fleet-management/drivers/${dId}`);
//                                     }}
//                                 />
//                             </Box>
//                         </CardBody>
//                     </Card>
//                 </VStack> 
//             </Box> 
//         </AdminLayout>
//     );
// };

// export default DriversPage;

import React, { useState, useCallback, useMemo, useRef } from 'react';
import { 
    Box, Heading, Text, HStack, VStack, InputGroup, Input, 
    InputRightElement, IconButton, Switch, Card, CardHeader, 
    CardBody, Badge, Wrap, WrapItem 
} from '@chakra-ui/react';
import { Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { apiClient } from '@/lib/api/client';
import { useToastNotification } from '@/lib/utils/useToastNotification';
import { DataTable, type DataTableRef, type DataTableOptions } from '@/components/tables';
import { useAccessCheck } from '@/hooks/useAccess';
import { useThemeColors } from '@/hooks/useThemeColors';
import { ActionButtons } from '@/components/common/ActionButtons';

const DriversPage: React.FC = () => {
    const navigate = useNavigate();
    const colors = useThemeColors();
    const access = useAccessCheck();
    const tableRef = useRef<DataTableRef>(null);
    const searchRef = useRef<HTMLInputElement>(null);
    
    const { showError, showSuccess } = useToastNotification();

    const [searchQuery, setSearchQuery] = useState('');
    const [updatingStatus, setUpdatingStatus] = useState<Record<number, boolean>>({});

    // Pattern: Explicit table params for reactive filtering
    const tableParams = useMemo(() => ({ search: searchQuery }), [searchQuery]);

    const handleSearch = () => {
        const query = searchRef.current?.value || '';
        setSearchQuery(query); 
        tableRef.current?.applyFilter('search', query); 
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') handleSearch();
    };

    const handleStatusToggle = useCallback(async (driverId: number, currentStatus: number) => {
        setUpdatingStatus((prev) => ({ ...prev, [driverId]: true }));
        try {
            const endpoint = currentStatus === 1 
                ? `/drivers/deactivate/${driverId}` 
                : `/drivers/activate/${driverId}`;
            
            await apiClient.patch(endpoint); 
            showSuccess('Success', `Driver status updated successfully.`);
            tableRef.current?.reload();
        } catch (error: any) {
            showError(error);
        } finally {
            setUpdatingStatus((prev) => {
                const updated = { ...prev };
                delete updated[driverId];
                return updated;
            });
        }
    }, [showSuccess, showError]);

    const tableOptions: DataTableOptions = useMemo(
        () => ({
            dataSource: '/drivers', 
            primaryKey: 'id',
            showSelection: false, 
            showCheckboxes: false, // Pattern: Explicitly disable to avoid layout shifts
            softDelete: true, 
            displayInModal: true, // Standardizing to page-based navigation
            noRecordText: 'No drivers found',
            
            dataFields: {
                first_name: { name: 'First Name', withSort: true },
                last_name: { name: 'Last Name', withSort: true },
                contact_number: { name: 'Contact Number', withSort: false, maxWidth: '150px' },
                assigned_truck_plate_numbers: { name: 'Assigned Trucks', withSort: false },
                is_active: { name: 'STATUS', withSort: false, maxWidth: '100px' },
            },

            actions: [
                {
                    label: 'Edit Driver',
                    action: 'Edit',
                    icon: 'Edit',
                    can_do: access.can_edit,
                },
            ],

            otherActions: [
              {
                name: 'Waybill History',
                link: '/fleet-management/waybill-history',
              },
              {
                name: 'Cash Advance History',
                link: '/fleet-management/ca-history',
              },
            ]
        }),
        [access.can_edit]
    );

    const customRenderers = useMemo(
        () => ({
            assigned_truck_plate_numbers: (value: string[]) => {
                if (!value || value.length === 0) return <Text color="gray.400" fontSize="xs">None</Text>;
                
                return (
                    <Wrap spacing={1}>
                        {value.map((plate) => (
                            <WrapItem key={plate}>
                                <Badge colorScheme="blue" variant="subtle" fontSize="xs" borderRadius="2px" px={2}>
                                    {plate}
                                </Badge>
                            </WrapItem>
                        ))}
                    </Wrap>
                );
            },
            is_active: (value: any, row: any) => {
                const statusValue = Number(value);
                const isUpdating = updatingStatus[row.id] || false;
                return (
                    <Switch
                        isChecked={statusValue === 1}
                        isDisabled={isUpdating || !access.can_edit}
                        colorScheme="blue" // Matching Yard/Truck toggle theme
                        onChange={() => handleStatusToggle(row.id, statusValue)}
                    />
                );
            },
        }),
        [updatingStatus, handleStatusToggle, access.can_edit]
    );

    const permissions = useMemo(() => ({
        can_edit: access.can_edit,
        can_create: access.can_create,
        can_delete: access.can_delete,
        can_view: access.can_view,
    }), [access]);

    return (
        <AdminLayout>
            <Box p={4} h="100%" overflow="hidden" display="flex" flexDirection="column">
                <VStack spacing={2} align="stretch" flex="1" overflow="hidden" minH={0}>
                    <Card flexShrink={0} display="flex" flexDirection="column" flex="1" minH={0} overflow="hidden">
                        
                        <CardHeader flexShrink={0} py={3}>
                            <Heading size="md" mb={1}>Driver Management</Heading>
                            <Text fontSize="sm" color={colors.text}>Manage drivers and their truck assignments.</Text>
                        </CardHeader>
                        
                        <CardHeader py={0} px={0} flexShrink={0}>
                            <Box pt={2} pb={0}>
                                <HStack justify="space-between" align="flex-end" spacing={4} px={5} width="100%">
                                    <Box flex="0 1 auto" minW="400px" maxW="400px">
                                        <InputGroup size="sm">
                                            <Input
                                                ref={searchRef}
                                                placeholder="Search drivers..."
                                                onKeyDown={handleKeyDown}
                                                defaultValue={searchQuery}
                                                pr="2.5rem"
                                            />
                                            <InputRightElement width="2.5rem">
                                                <IconButton
                                                    aria-label="Search"
                                                    icon={<Search size={16} />}
                                                    size="sm"
                                                    h="1.75rem"
                                                    w="1.75rem"
                                                    onClick={handleSearch}
                                                    variant="ghost"
                                                />
                                            </InputRightElement>
                                        </InputGroup>
                                    </Box>

                                    <Box>
                                        <ActionButtons pathname="/fleet-management/drivers" createLabel="Add New Driver" />
                                    </Box>
                                </HStack>
                                <Box mx={4} height={4} />
                            </Box>
                        </CardHeader>
                        
                        <CardBody
                            p={0}
                            pt={1}
                            pb={4}
                            mx={5}
                            flex="1"
                            overflow="hidden"
                            display="flex"
                            flexDirection="column"
                            minH={0}
                            position="relative"
                        >
                            <Box h="100%" display="flex" flexDirection="column" minH={0}>
                                <DataTable
                                    ref={tableRef}
                                    options={tableOptions}
                                    params={tableParams}
                                    access={permissions}
                                    customRenderers={customRenderers}
                                    onOpenModal={(idObj) => {
                                        const dId = typeof idObj === 'object' ? idObj.id : idObj;
                                        if (dId) navigate(`/fleet-management/drivers/${dId}`);
                                    }}
                                />
                            </Box>
                        </CardBody>
                    </Card>
                </VStack> 
            </Box> 
        </AdminLayout>
    );
};

export default DriversPage;